import React, { useMemo } from "react"

import useFormatMessage from "decentraland-gatsby/dist/hooks/useFormatMessage"
import { Link } from "decentraland-gatsby/dist/plugins/intl"
import TokenList from "decentraland-gatsby/dist/utils/dom/TokenList"
import {
  Dropdown,
  DropdownProps,
} from "decentraland-ui/dist/components/Dropdown/Dropdown"

import { IBlogAuthorFields } from "../../@types/contentful"
import Search from "../../components/Blog/Search/Search"
import { isWebpSupported, useImageOptimization } from "../../hooks/contentful"
import { useBlogContentfulQuery } from "../../hooks/useBlogContentfulQuery"
import locations from "../../modules/locations"
import Image from "../Contentful/Image"

import "./BlogNavigation.css"

export type BlogNavigationProps = React.HTMLAttributes<HTMLDivElement> & {
  active: string
}

export default React.memo(function BlogNavigation({
  active,
  ...props
}: BlogNavigationProps) {
  const l = useFormatMessage()
  const { getBlogCategoriesToShow } = useBlogContentfulQuery()
  const categories = useMemo(getBlogCategoriesToShow, [])

  return (
    <div
      {...props}
      className={TokenList.join(["ui container-wrapper", props.className])}
    >
      <div className="navbar__content">
        <div className="navbar__wrapper">
          <div className="navbar__categories">
            <ul>
              <li key="all_items">
                <Link
                  href={locations.blogs()}
                  className={TokenList.join([
                    active === "all_articles" && "active",
                  ])}
                >
                  {l("component.nav_menu.all_articles")}
                </Link>
              </li>
              {categories?.map((category) => (
                <li key={category.id}>
                  <Link
                    href={locations.category(category.id)}
                    className={TokenList.join([
                      active === category.id && "active",
                    ])}
                  >
                    {category.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <Search />
        </div>
      </div>
    </div>
  )
})

export type AuthorsDropdownProps = Omit<DropdownProps, "children"> & {
  active?: string
  authors?: IBlogAuthorFields[] | null
}

export const AuthorsDropdown = React.memo(function ({
  authors,
  active,
  ...props
}: AuthorsDropdownProps) {
  return (
    <Dropdown {...props}>
      <Dropdown.Menu className="options-wrapper">
        {authors
          ?.sort((a, b) => (a.title < b.title ? -1 : 1))
          .map((author) => (
            <AuthorsDropdownItem
              key={author.id}
              author={author}
              active={active === author.id}
            />
          ))}
      </Dropdown.Menu>
    </Dropdown>
  )
})

export type AuthorsDropdownItemProps = {
  active?: boolean
  author: IBlogAuthorFields
}

export const AuthorsDropdownItem = React.memo(function ({
  author,
  active,
}: AuthorsDropdownItemProps) {
  const optimizedImage = useImageOptimization(author.image?.fields?.file?.url)
  return (
    <Dropdown.Item key={author.id}>
      <Link
        href={locations.author(author.id)}
        className={TokenList.join([active && "active"])}
      >
        <Image
          format={isWebpSupported() ? `webp` : `jpg`}
          src={optimizedImage.optimized}
          alt={author.title}
        />
        <span>{author.title}</span>
      </Link>
    </Dropdown.Item>
  )
})
